import { defineStore } from "pinia";
import { useSettings } from "./settings";
import { useAuth } from "./auth";
import { IEvent } from "~/types/sports/event";
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";
import { IPrice } from "~/types/sports/price";
import { IScore } from "~/types/sports/score";
import api from "~/utils/auth/api";
import { useBetbook } from "./betbook";
import { useBonusses } from "./bonusses";
import { ISport } from "~/types/sports/sport";
import { InsureCalc } from "@osiris/horus.betwayinsurance.web.calcs";
import { IBet } from "~/types/universal/bet";
import { StrikeBet } from "~/types/sports/strike";

const INSURANCE_EXCLUDED_MARKETS = ["outright", "draw-no-bet"];

export interface IBetslipState {
	activeTab: "single" | "multi";
	open: boolean;
	// Cash: 1
	// FreeBet: 2
	// SportsBonus: 4
	multiBetPayment: 1 | 2 | 4;
	linkedEvents: string[];
	selection: {
		outcomeId: string;
		eventId: number;
		marketId: string;
		value: number;
		selected: boolean;
		payment: 1 | 2 | 4;
		canPlaceSportsBonusBet: boolean;
		isSportBonusAllowed: boolean;
		meetsMinSportsBonusOdds: boolean;
		isStartingPrice?: boolean | false;
		isFirst?: boolean;
		isLast?: boolean;
		isLinkedFirst?: boolean;
		isLinkedLast?: boolean;
		source?: string | null;
	}[];
	// Build a Bet Selections
	buildSelections: {
		isEachWay: boolean;
		priceDec: number;
		priceNum: number;
		priceDen: number;
		selected: boolean;
		useFreeBet: boolean;
		numberOfLines: number;
		payment: 1 | 2 | 4;
		value: number;
		selections: { outcomeId: string; eventId: number; marketId: string; isStartingPrice?: boolean }[];
		suspended?: boolean;
	}[];
	events: Map<number, IEvent>;
	markets: Map<string, IMarket>;
	outcomes: Map<string, IOutcome>;
	prices: Map<string, IPrice>;
	scores: Map<number, IScore>;
	personalizedMenu: string[];
	multiWagerAmount: number;
	keepBetsInBetslip: boolean;
	autoAcceptLatestOdds: boolean | null;
	simpleView: boolean;
	recentSearches: { name: string; id: string | null; type?: string }[];
	feedsModal: boolean;
	feedsProfileModal: boolean;
	feedsInfoModal: boolean;
	forceSingleTab: boolean;
	postModal: false | IBet | StrikeBet;
}

export const useBetslip = defineStore({
	id: "betslip",
	state: (): IBetslipState => ({
		activeTab: "single",
		open: false,
		multiBetPayment: 1,
		selection: [],
		buildSelections: [],
		events: new Map(),
		markets: new Map(),
		outcomes: new Map(),
		prices: new Map(),
		scores: new Map(),
		personalizedMenu: [],
		multiWagerAmount: 0,
		recentSearches: [],
		keepBetsInBetslip: false,
		autoAcceptLatestOdds: null,
		simpleView: true,
		linkedEvents: [],
		feedsModal: false,
		forceSingleTab: false,
		feedsProfileModal: false,
		feedsInfoModal: false,
		postModal: false,
	}),
	actions: {
		setEvent(event: IEvent) {
			const storeEvent = this.events.get(event.eventId);
			if (event.version > 0 ? event.version > (storeEvent?.version || 0) : true) {
				const updatedEvent = { ...event, homeTeam: storeEvent?.homeTeam || event.homeTeam, awayTeam: storeEvent?.awayTeam || event.awayTeam };
				this.events.set(event.eventId, updatedEvent);
			}
		},
		setMarket(market: IMarket) {
			const storeMarket = this.markets.get(market.marketId);
			if (market.version > 0 ? market.version > (storeMarket?.version || 0) : true) {
				this.markets.set(market.marketId, market);
			}
		},
		setOutcome(outcome: IOutcome) {
			const storeOutcome = this.outcomes.get(outcome.outcomeId);
			if (outcome.version > 0 ? outcome.version > (storeOutcome?.version || 0) : true) {
				this.outcomes.set(outcome.outcomeId, outcome);
			} else if (outcome.version === storeOutcome?.version) {
				this.outcomes.set(outcome.outcomeId, { ...storeOutcome, sbv: outcome.sbv, handicap: outcome.handicap });
			}
		},
		setPrice(price: IPrice) {
			const storePrice = this.prices.get(price.outcomeId);
			if (price.version > 0 ? price.version >= (storePrice?.version || 0) : true) this.prices.set(price.outcomeId, price);
		},
		builAbetCheck() {
			setInterval(() => {
				this.buildSelections.forEach((bb, i) => {
					this.rebuildBet(
						bb.selections.map((s) => s.outcomeId),
						i
					);
				});
			}, 10000);
		},
		rebuildBet(selections: string[], index: number) {
			const settings = useSettings();
			interface BuildData {
				systemCName: string;
				canPlaceBet: boolean;
				possibleWinnings: number;
				possibleWinningsNum: number;
				possibleWinningsDen: number;
				numLines: number;
				useFreeBet: boolean;
				eachWayActive: boolean;
				conflictingOutcomeIds: number[];
			}
			api<BuildData>({
				url: "/Betting/Build",
				type: "betting",
				options: {
					method: "POST",
				},
				data: {
					betSelectionType: 2,
					countryCode: settings.getCountryConfig?.countryIsoTwo,
					currencyCode: settings.getCurrencyConfig?.currencyCode,
					outcomeIds: selections,
				},
			}).then((res) => {
				if (res.canPlaceBet) {
					this.buildSelections[index].priceDec = res.possibleWinnings;
					this.buildSelections[index].priceNum = res.possibleWinningsNum;
					this.buildSelections[index].priceDen = res.possibleWinningsDen;
					this.buildSelections[index].suspended = false;
				} else {
					this.buildSelections[index].suspended = true;
				}
			});
		},
		setScore(score: IScore) {
			this.scores.set(score.eventId, score);
		},

		handleMultipleSelections(
			selections: {
				event: IEvent | undefined | null;
				market: IMarket | undefined | null;
				outcome: IOutcome | undefined | null;
				price: IPrice | undefined | null;
				score: IScore | undefined | null;
			}[]
		) {
			const settings = useSettings();
			const auth = useAuth();

			const newSelections: {
				outcomeId: string;
				eventId: number;
				marketId: string;
				value: number;
				selected: boolean;
				payment: 1 | 2 | 4;
				canPlaceSportsBonusBet: boolean;
				isSportBonusAllowed: boolean;
				meetsMinSportsBonusOdds: boolean;
				isStartingPrice: boolean;
			}[] = [];

			selections.forEach((selection) => {
				if (window.innerWidth >= 1280) {
					this.open = true;
				}

				if (this.isSelectionActive(selection?.outcome?.outcomeId)) {
					if (selection.outcome) {
						const index = this.selection.findIndex((s) => s.outcomeId === selection.outcome?.outcomeId);
						this.selection.splice(index, 1);
					}
				} else if (!this.hasReachedMaxBetsInBetslip() && !selection.event?.isFinished) {
					if (selection.event && selection.outcome && selection.market) {
						const sportsBonusEnabled = settings.hasFeature("IsSportsBonusEnabled");
						let isSportBonusAllowed = false;
						let meetsMinSportsBonusOdds = false;
						let canPlaceSportsBonusBet = false;

						if (sportsBonusEnabled) {
							const bonusses = useBonusses();
							const betbook = useBetbook();

							const sport = betbook.sportsBonusAllowedSports.find((s: ISport) => s.sportId === selection.event?.sportId);
							isSportBonusAllowed =
								(sport?.isSportBonusAllowed && selection.event.isSportBonusAllowed && selection.market.isSportBonusAllowed) || false;

							const minSportsBonusOdds = bonusses.activeSportBonus?.minOdds;
							meetsMinSportsBonusOdds = minSportsBonusOdds && selection.price ? selection.price.priceDecimal >= minSportsBonusOdds : false;
							canPlaceSportsBonusBet = isSportBonusAllowed && meetsMinSportsBonusOdds;
						}

						if (selection.event && selection.outcome && selection.market) {
							newSelections.push({
								outcomeId: selection.outcome.outcomeId,
								eventId: selection.event?.eventId,
								marketId: selection.market?.marketId,
								value: auth.profile?.defaultBetSize || settings.config?.appSetting?.MinimumBetAmount || 0,
								selected:
									this.activeTab === "multi"
										? !this.hasReachedMaxBetsStrikeMulti()
										: this.activeTab === "single"
										? !this.hasReachedMaxBetsStrikeSingle()
										: true,
								payment: 1,
								canPlaceSportsBonusBet,
								isSportBonusAllowed,
								meetsMinSportsBonusOdds,
								isStartingPrice: false,
							});
						}
					}
					if (selection.event) this.events.set(selection.event.eventId, selection.event);
					if (selection.market) this.markets.set(selection.market.marketId, selection.market);
					if (selection.outcome) this.outcomes.set(selection.outcome.outcomeId, selection.outcome);
					if (selection.outcome && selection.price) this.prices.set(selection.outcome.outcomeId, selection.price);
					if (selection.score && selection.event) this.scores.set(selection.event.eventId, selection.score);

					this.selection = newSelections;
				}
			});

			const newOutcomeIds = this.selection.map((s) => s.outcomeId);
			if (newOutcomeIds.length > 0) this.fetchLatestOutcomes(newOutcomeIds);
		},
		async handleSelection(
			selection: {
				event: IEvent | undefined | null;
				market: IMarket | undefined | null;
				outcome: IOutcome | undefined | null;
				price: IPrice | undefined | null;
				score?: IScore | undefined | null;
				isStartingPrice?: boolean | false;
				source?: string | null;
			},
			editOutcomeId?: string | undefined,
			updateBetbook = true
		) {
			const settings = useSettings();
			const auth = useAuth();
			const betbook = useBetbook();
			const { $trackEvent } = useNuxtApp();
			const trackRacingSports = ["horse-racing", "greyhounds"];

			const firstSelectionEmopSportId = this.selection.length ? (this.events.get(this.selection[0].eventId)?.sportId as string) : "";
			if (
				this.isTrackRacingBetslip &&
				firstSelectionEmopSportId &&
				trackRacingSports.includes(firstSelectionEmopSportId) &&
				!trackRacingSports.includes(selection.event?.sportId as string)
			) {
				this.selection = [];
			} else if (
				!this.isTrackRacingBetslip &&
				firstSelectionEmopSportId !== "horse-racing" &&
				!trackRacingSports.includes(firstSelectionEmopSportId) &&
				trackRacingSports.includes(selection.event?.sportId as string)
			) {
				this.selection = [];
			}

			if (this.isSelectionActive(selection?.outcome?.outcomeId)) {
				if (selection.outcome) {
					if (this.linkedEvents.includes(selection.outcome?.outcomeId)) {
						this.linkedEvents = [];
					}

					const index = this.selection.findIndex((s) => s.outcomeId === selection.outcome?.outcomeId);
					const clone = [...this.selection];
					clone.splice(index, 1);

					if (editOutcomeId && editOutcomeId === selection.outcome?.outcomeId) {
						const router = useRouter();
						const route = useRoute();
						const copy: { [x: string]: string } = {};
						Object.assign(copy, route.query);
						delete copy["isEditingBet"];
						delete copy["outcomeId"];
						await router.push({
							query: {
								...copy,
							},
						});
					}

					if (!clone.length) {
						if (!this.activeBuildSelections.length) {
							this.open = false;
							await Promise.all([
								(this.events = new Map()),
								(this.markets = new Map()),
								(this.outcomes = new Map()),
								(this.prices = new Map()),
								(this.scores = new Map()),
							]);
						} else this.activeTab = "single";
					} else if (window.innerWidth > 1280) this.open = true;
					this.selection = clone;
				}
			} else if (!this.hasReachedMaxBetsInBetslip() && !selection.event?.isFinished) {
				if (window.innerWidth > 1280) this.open = true;

				const isReplaceOutcomeEnabled = settings.hasFeature("IsReplaceOutcomeEnabled");

				if (
					this.selection.filter((x) => x.eventId === selection.event?.eventId && x.outcomeId !== selection.outcome?.outcomeId).length === 1 &&
					isReplaceOutcomeEnabled &&
					!this.forceSingleTab
				) {
					this.selection = this.selection.filter((x) => x.eventId !== selection.event?.eventId && x.outcomeId !== selection.outcome?.outcomeId);
					this.activeTab = "multi";
				}

				if (selection.event && selection.outcome && selection.market) {
					$trackEvent.addBet({
						eventName: selection.event.name,
						marketName: selection.market.displayName,
						outcomeName: selection.outcome.displayName,
						priceDecimal: selection.price?.priceDecimal,
						source: "Betbook",
						sportId: selection.event.sportId,
					});
					const sportsBonusEnabled = settings.hasFeature("IsSportsBonusEnabled");

					let isSportBonusAllowed = false;
					let meetsMinSportsBonusOdds = false;
					let canPlaceSportsBonusBet = false;

					if (sportsBonusEnabled) {
						const bonusses = useBonusses();
						const betbook = useBetbook();

						const sport = betbook.sportsBonusAllowedSports.find((s: ISport) => s.sportId === selection.event?.sportId);
						isSportBonusAllowed =
							(sport?.isSportBonusAllowed && selection.event.isSportBonusAllowed && selection.market.isSportBonusAllowed) || false;

						const minSportsBonusOdds = bonusses.activeSportBonus?.minOdds;
						meetsMinSportsBonusOdds = minSportsBonusOdds && selection.price ? selection.price.priceDecimal >= minSportsBonusOdds : false;
						canPlaceSportsBonusBet = isSportBonusAllowed && meetsMinSportsBonusOdds;
					}

					const selectionData = {
						outcomeId: selection.outcome.outcomeId,
						eventId: selection.event?.eventId,
						marketId: selection.market?.marketId,
						value: auth.profile?.defaultBetSize || settings.config?.appSetting?.MinimumBetAmount || 0,
						payment: 1 as 1 | 2 | 4,
						selected:
							this.activeTab === "multi"
								? !this.hasReachedMaxBetsStrikeMulti()
								: this.activeTab === "single"
								? !this.hasReachedMaxBetsStrikeSingle()
								: true,
						canPlaceSportsBonusBet,
						isSportBonusAllowed,
						meetsMinSportsBonusOdds,
						isStartingPrice: selection.isStartingPrice ? selection.isStartingPrice : false,
						source: selection.source,
					};

					if (editOutcomeId && !isReplaceOutcomeEnabled) {
						const editIndex = this.selection.findIndex((s) => s.outcomeId === editOutcomeId);
						if (editIndex !== -1) {
							this.selection[editIndex] = selectionData;
							const route = useRoute();
							const router = useRouter();
							router.replace({ query: { ...route.query, outcomeId: selection.outcome?.outcomeId } });
						}
					} else {
						this.selection.push(selectionData);
					}

					if (!editOutcomeId && !this.forceSingleTab && !isReplaceOutcomeEnabled) {
						if (this.selection.length > 1) {
							this.activeTab = "multi";
						} else {
							this.activeTab = "single";
						}
					}
				}
				if (selection.event) this.events.set(selection.event.eventId, selection.event);
				if (selection.market) {
					this.markets.set(selection.market.marketId, selection.market);
					if (selection.outcome?.originalMarketId) {
						const originalMarket = betbook.markets.get(selection.outcome?.originalMarketId || "");
						if (originalMarket) this.markets.set(selection.outcome?.originalMarketId, originalMarket);
					}
				}
				if (selection.outcome) this.outcomes.set(selection.outcome.outcomeId, selection.outcome);
				if (selection.outcome && selection.price) this.prices.set(selection.outcome.outcomeId, selection.price);
				if (selection.score && selection.event) this.scores.set(selection.event.eventId, selection.score);

				if (selection.outcome?.outcomeId) this.fetchLatestOutcomes([selection.outcome?.outcomeId], updateBetbook);
			}
		},
		fetchLatestOutcomes(outcomeIds: string[], updateBetbook = true) {
			try {
				const settings = useSettings();
				const betbook = useBetbook();

				let queryParams = `CountryCode=${settings.getCountryConfig?.countryIsoTwo}&CultureCode=${settings.locale}`;
				for (let index = 0; index < outcomeIds.length; index++) {
					const OutcomeId = outcomeIds[index];
					queryParams += `&OutcomeIds=${OutcomeId}`;
				}

				api<{ events: IEvent[]; markets: IMarket[]; outcomes: IOutcome[]; prices: IPrice[]; scores: IScore[] }>({
					type: this.isTrackRacingBetslip ? "horses" : "sports",
					url: "/FeedsOutcome/emop/?" + queryParams,
					options: { method: "GET" },
				}).then((res) => {
					res.outcomes.forEach((outcome) => {
						const event = res.events?.find((x) => x.eventId === outcome.eventId);
						// const market = res.markets?.find((x) => x.marketId === outcome.marketId);
						const price = res.prices?.find((x) => x.outcomeId === outcome.outcomeId);
						const score = res.scores?.find((x) => x.eventId === outcome.eventId);
						if (event && updateBetbook) {
							this.setEvent(event);
							if (updateBetbook) betbook.setEvent(event);
						}
						res.markets.forEach((market) => {
							this.setMarket(market);
							if (updateBetbook) betbook.setMarket(market);
						});
						if (price) {
							this.setPrice(price);
							if (updateBetbook) betbook.setPrice(price);
						}
						if (score) {
							this.setScore(score);
							if (updateBetbook) betbook.setScore(score);
						}
						this.setOutcome(outcome);
						if (updateBetbook) betbook.setOutcome(outcome);
					});
				});
			} catch (e) {}
		},
	},
	getters: {
		isTrackRacingBetslip(state) {
			const route = useRoute();
			const firstSelectionEmopSportId = state.selection.length ? (state.events.get(state.selection[0].eventId)?.sportId as string) : "";
			const isHorseRacingBetslip = ["horse-racing", "greyhounds"].includes(firstSelectionEmopSportId);
			//Accomodate booking code if the bet selection is empty where firstSelectionEmopSportId is empty
			return isHorseRacingBetslip || (!firstSelectionEmopSportId && ["horse-racing", "greyhounds"].includes(route.params.sportId as string));
		},
		betslipConfigCount(state) {
			const auth = useAuth();
			const autoAccept = state.autoAcceptLatestOdds !== null ? state.autoAcceptLatestOdds : auth.profile?.autoAcceptLatestOdds;
			const config = [state.keepBetsInBetslip, autoAccept];
			return config.filter((x) => x).length;
		},
		activeSelections: (state) => {
			const betbook = useBetbook();
			const setting = useSettings();
			const isSuspended = ({ outcomeId, marketId, eventId }: { outcomeId: string; marketId: string; eventId: number }) => {
				const outcome = state.outcomes?.get(outcomeId);
				const price = state.prices?.get(outcomeId);
				const market = state.markets?.get(outcome?.originalMarketId || marketId);
				const event = state.events?.get(eventId);
				if (
					outcome?.isTradingActive === false ||
					outcome?.shouldDisplay === false ||
					(!price?.priceDecimal && market?.additionalInfo?.UseFixedPrice === "True") ||
					price?.priceDecimal === 1 ||
					market?.isSuspended ||
					(event && !betbook.isEventActive(event)) ||
					!event?.shouldDisplay ||
					!market?.shouldDisplay
				) {
					return true;
				}
				return false;
			};

			return state.selection.filter(
				(x) =>
					x.selected &&
					!isSuspended({
						eventId: x.eventId,
						marketId: x.marketId,
						outcomeId: x.outcomeId,
					})
			);
		},
		activeBuildSelections: (state) => {
			return state.buildSelections.filter((x) => x.selected);
		},
		betslipCount() {
			const activeBuildSelections: number = this.buildSelections.filter((x) => x.selected && x.suspended === false).length || 0;
			if (this.activeTab === "single") return this.activeSelections.length + activeBuildSelections;
			else return this.activeSelections.length;
		},
		isBetslipEmpty: (state) => {
			if (state.activeTab === "single") return state.selection.length + state.buildSelections.length <= 0;
			else return state.selection.length <= 0;
		},
		winBoostConfig() {
			const settings = useSettings();
			const route = useRoute();
			const winboostSettings = settings.config?.winBoostConfig;
			if (winboostSettings && winboostSettings?.ExcludedSports && winboostSettings?.ExcludedSports.includes(route.params.sportId as string))
				return undefined;
			if (winboostSettings) {
				let eligibleBets = 0;
				this.activeSelections.forEach((bet) => {
					const price = this.prices.get(bet.outcomeId);
					const market = this.markets.get(bet.marketId);
					if (
						price &&
						winboostSettings &&
						price?.priceDecimal >= winboostSettings?.MinOddsPerEvent &&
						bet.selected &&
						!winboostSettings?.ExcludedMarkets?.includes(market?.marketTypeCName || "")
					) {
						eligibleBets++;
					}
				});

				if (eligibleBets > winboostSettings.WinBoostOffers.length) {
					return winboostSettings.WinBoostOffers[winboostSettings.WinBoostOffers.length - 1];
				}
				return winboostSettings.WinBoostOffers.filter((x) => x.Events === eligibleBets)[0] || { WinBoostValue: 0 };
			} else return undefined;
		},
		hasReachedMaxBetsInBetslip: (state) => () => {
			const config = useRuntimeConfig();
			return state.selection.length >= Number(config.public.maximumBetsInBetslip);
		},
		hasReachedMaxBetsStrikeMulti: (state) => () => {
			const config = useRuntimeConfig();
			const activeSelections = state.selection.filter((s) => s.selected);
			return state.activeTab === "multi" && activeSelections.length >= Number(config.public.maximumBetsStrikeMulti);
		},
		hasReachedMaxBetsStrikeSingle: (state) => () => {
			const config = useRuntimeConfig();
			const activeSelections = state.selection.filter((s) => s.selected);
			return state.activeTab === "single" && activeSelections.length >= Number(config.public.maximumBetsStrikeSingles);
		},
		isSelectionActive: (state) => (outcomeId: string | undefined) => {
			return outcomeId && state.selection.find((s) => s.outcomeId === outcomeId);
		},
		// Insurance Calculations
		activeInsuranceBets() {
			const bets: { marketTypeCName: string; priceDecimal: number }[] = [];
			this.activeSelections.forEach((s) => {
				const event = this.events.get(s.eventId);
				const market = this.markets.get(s.marketId);
				const outcome = this.outcomes.get(s.outcomeId);
				const price = this.prices.get(s.outcomeId);
				if (event && market && outcome && price && price.priceDecimal > 0) {
					bets.push({
						priceDecimal: price.priceDecimal,
						marketTypeCName: market.marketTypeCName,
					});
				}
			});
			return bets;
		},
		insuranceCalculator: () => {
			const settings = useSettings();
			if (!settings.insurance) return null;
			try {
				return new InsureCalc({
					...settings.insurance,
					moneyBackBoost: settings.insurance.moneyBackBoost || [],
				});
			} catch (e) {
				return null;
			}
		},
		hasInvalidInsuranceBets() {
			const hasInvalidMarkets = this.activeInsuranceBets.some(({ marketTypeCName }) => INSURANCE_EXCLUDED_MARKETS.includes(marketTypeCName));
			if (hasInvalidMarkets) return true;
			return false;
		},
		insurancePayouts(): { winningLegs: number; payoutAmount: number }[] {
			try {
				const settings = useSettings();
				if (!this.insuranceCalculator || this.hasInvalidInsuranceBets) return [];
				const legs = this.activeInsuranceBets.length;
				const priceDecimals = this.activeInsuranceBets.map(({ priceDecimal }) => priceDecimal);
				const { payout } = this.insuranceCalculator.getQuote({
					priceDecimals,
					wageredAmount: this.multiWagerAmount,
					brandId: settings.config?.appSetting?.BrandId || "",
				});
				const insurancePayouts = payout.filter((x) => x !== 0);
				return insurancePayouts.map((payoutAmount, index) => {
					return {
						winningLegs: legs - (index + 1),
						payoutAmount,
					};
				});
			} catch (e) {
				return [];
			}
		},
	},
	persist: {
		paths: [
			"selection",
			"buildSelections",
			"activeTab",
			"personalizedMenu",
			"recentSearches",
			"keepBetsInBetslip",
			"autoAcceptLatestOdds",
			"simpleView",
		],
		storage: persistedState.localStorage,
	},
});
